/**
 * The navigation column's glyphs, as the public portals draw them (DX-352).
 *
 * The Developer Portal and Help draw their sidebar with Lucide line icons at
 * 14px (`.docs-sidebar-icon`); Material's filled shapes at 20px were the most
 * visible difference between their column and this one. Inlined for the same
 * reason as `barIcons.tsx`: Backstage ships no Lucide set, and these are
 * copied from `@iconify-json/lucide` in services/developer-portal rather than
 * redrawn.
 *
 * Each is `aria-hidden`: the row's text is what a reader and a screen reader
 * go by, and the glyph only repeats it.
 */
import { ReactNode } from 'react';

export type NavIcon = () => JSX.Element;

const glyph =
  (children: ReactNode): NavIcon =>
  () => (
    <svg
      width={14}
      height={14}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      {children}
    </svg>
  );

// i-lucide-house
export const HouseIcon = glyph(
  <>
    <path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8" />
    <path d="M3 10a2 2 0 0 1 .709-1.528l7-6a2 2 0 0 1 2.582 0l7 6A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
  </>,
);

// i-lucide-message-circle-question
export const AskIcon = glyph(
  <>
    <path d="M2.992 16.342a2 2 0 0 1 .094 1.167l-1.065 3.29a1 1 0 0 0 1.236 1.168l3.413-.998a2 2 0 0 1 1.099.092a10 10 0 1 0-4.777-4.719" />
    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3m.08 4h.01" />
  </>,
);

// i-lucide-circle-plus
export const CreateIcon = glyph(
  <>
    <circle cx="12" cy="12" r="10" />
    <path d="M8 12h8m-4-4v8" />
  </>,
);

// i-lucide-shapes
export const CatalogIcon = glyph(
  <>
    <path d="M8.3 10a.7.7 0 0 1-.626-1.079L11.4 3a.7.7 0 0 1 1.198-.043L16.3 8.9a.7.7 0 0 1-.572 1.1Z" />
    <rect width="7" height="7" x="3" y="14" rx="1" />
    <circle cx="17.5" cy="17.5" r="3.5" />
  </>,
);

// i-lucide-book-open
export const DocsIcon = glyph(
  <path d="M12 7v14m-9-3a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4a4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3a3 3 0 0 0-3-3z" />,
);

// i-lucide-code
export const ApiIcon = glyph(<path d="m16 18l6-6l-6-6M8 6l-6 6l6 6" />);

// i-lucide-network
export const ResponsibilityIcon = glyph(
  <>
    <rect width="6" height="6" x="16" y="16" rx="1" />
    <rect width="6" height="6" x="2" y="16" rx="1" />
    <rect width="6" height="6" x="9" y="2" rx="1" />
    <path d="M5 16v-3a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v3m-7-4V8" />
  </>,
);

// i-lucide-layout-grid
export const AreasIcon = glyph(
  <>
    <rect width="7" height="7" x="3" y="3" rx="1" />
    <rect width="7" height="7" x="14" y="3" rx="1" />
    <rect width="7" height="7" x="14" y="14" rx="1" />
    <rect width="7" height="7" x="3" y="14" rx="1" />
  </>,
);

// i-lucide-activity
export const StatusIcon = glyph(
  <path d="M22 12h-2.48a2 2 0 0 0-1.93 1.46l-2.35 8.36a.25.25 0 0 1-.48 0L9.24 2.18a.25.25 0 0 0-.48 0l-2.35 8.36A2 2 0 0 1 4.49 12H2" />,
);

// i-lucide-rocket
export const DeploymentsIcon = glyph(
  <>
    <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09" />
    <path d="M9 12a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.4 22.4 0 0 1-4 2z" />
    <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 .05 5 .05" />
  </>,
);

// i-lucide-circle-check
export const ScorecardsIcon = glyph(
  <>
    <circle cx="12" cy="12" r="10" />
    <path d="m9 12l2 2l4-4" />
  </>,
);

// i-lucide-shield
export const SecurityIcon = glyph(
  <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />,
);

// i-lucide-key-round
export const AccessIcon = glyph(
  <>
    <path d="M2.586 17.414A2 2 0 0 0 2 18.828V21a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h1a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h.172a2 2 0 0 0 1.414-.586l.814-.814a6.5 6.5 0 1 0-4-4z" />
    <circle cx="16.5" cy="7.5" r=".5" fill="currentColor" />
  </>,
);

// i-lucide-zap
export const JamIcon = glyph(
  <path d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z" />,
);

// i-lucide-terminal
export const TerminalIcon = glyph(<path d="M12 19h8M4 17l6-6l-6-6" />);

// i-lucide-star
export const StarIcon = glyph(
  <path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.12 2.12 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.12 2.12 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.12 2.12 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.12 2.12 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.12 2.12 0 0 0 1.597-1.16z" />,
);

// i-lucide-chevron-down
export const ChevronDownIcon = glyph(<path d="m6 9l6 6l6-6" />);

// i-lucide-bell
export const BellIcon = glyph(
  <path d="M10.268 21a2 2 0 0 0 3.464 0m-10.47-5.674A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.673C19.41 13.956 18 12.499 18 8A6 6 0 0 0 6 8c0 4.499-1.411 5.956-2.738 7.326" />,
);
